<template>
  <div>
    <!-- Company name header matching ViewCompany style -->
    <div class="mb-0 header py-4 bg-white">
      <b-container>
        <b-row>
          <b-col>
            <h2 class="mb-0">{{ company.name }}</h2>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!-- Tab navigation — same style as ViewCompany -->
    <div class="bg-jungle-mist">
      <b-container>
        <b-row>
          <b-col>
            <b-list-group horizontal="md" class="list-group-nav">
              <b-list-group-item
                :class="{ active: activeTab === 'users' }"
                class="border-0"
              >
                <a href="#" @click.prevent="activeTab = 'users'">Users</a>
              </b-list-group-item>
              <b-list-group-item
                v-if="!isUnderwriter"
                :class="{ active: activeTab === 'cash_call' }"
                class="border-0"
              >
                <a href="#" @click.prevent="activeTab = 'cash_call'">Cash Call</a>
              </b-list-group-item>
              <b-list-group-item
                :class="{ active: activeTab === 'billing' }"
                class="border-0"
              >
                <a href="#" @click.prevent="activeTab = 'billing'">Billing</a>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!-- Tab content -->
    <div>
      <b-container class="my-2">

        <!-- Users tab -->
        <template v-if="activeTab === 'users'">
          <TableUsers />
          <ModalAssignCompany />
          <ModalCompanyRole />
          <ModalUserName />
          <UserTypeModal />
          <AddUserModal
            @update:show="(value) => (showAddUserModal = value)"
            :show="showAddUserModal"
            :create-superuser="false"
            :for-company="company.id"
          />
          <UserReportsModal
            @update:show="(value) => (showUserReportsModal = value)"
            :show="showUserReportsModal"
            :user-id="selectedUserId"
          />
        </template>

        <!-- Cash Call tab -->
        <template v-if="activeTab === 'cash_call' && !isUnderwriter">
          <ManageCompanyCashCall :company="company" />
        </template>

        <!-- Billing tab -->
        <template v-if="activeTab === 'billing'">
          <CompanyBillingInvoicing :company="company" />
        </template>

      </b-container>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'

import { useAuth } from '@composables/auth'
import Members from '@services/Members'
import { UserTypeModal } from '@components/user'

import TableUsers from '@components/TableUsers'
import ModalAssignCompany from '@components/ModalAssignCompany'
import ModalCompanyRole from '@components/ModalCompanyRole'
import ModalUserName from '@components/ModalUserName'
import AddUserModal from '@components/AddUserModal'
import UserReportsModal from '@components/UserReportsModal'
import ManageCompanyCashCall from '@pages/company/ManageCompanyCashCall.vue'
import CompanyBillingInvoicing from '@pages/company/CompanyBillingInvoicing.vue'

export default defineComponent({
  name: 'CompanyAdminUsers',
  components: {
    UserTypeModal,
    TableUsers,
    ModalAssignCompany,
    ModalUserName,
    ModalCompanyRole,
    AddUserModal,
    UserReportsModal,
    ManageCompanyCashCall,
    CompanyBillingInvoicing
  },

  props: {
    company: {
      type: Object,
      required: true
    }
  },

  setup(props) {
    const { useMutations } = createVuexHelpers()
    const { READ_MEMBER } = useMutations(['READ_MEMBER'])
    const { currentUser } = useAuth()

    const activeTab = ref('users')
    const showAddUserModal = ref(false)
    const showUserReportsModal = ref(false)
    const selectedUserId = ref(0)

    // Underwriter company admins reach this same component (see
    // Users.vue's isCompanyAdmin) but only get Users + Billing — no Cash
    // Call tab, unlike TPA admins.
    const isUnderwriter = computed(() => (
      props.company && props.company.role && props.company.role.role === 'Underwriter'
    ))

    onMounted(() => {
      Vue.prototype.$bus.trackEvent('toggle-modal', async (payload) => {
        if (['user-report-access'].includes(payload.key)) {
          const resp = await Members.readMember(payload.id)
          const member = resp.data.data.data
          READ_MEMBER({ data: member })
        }

        if ('add-user' === payload.key) {
          showAddUserModal.value = payload.active
        } else if ('user-report-access' === payload.key) {
          showUserReportsModal.value = payload.active
          selectedUserId.value = payload.id
        }
      })
    })

    onUnmounted(() => {
      Vue.prototype.$bus.destroyEvents()
    })

    return {
      currentUser,
      activeTab,
      isUnderwriter,
      showAddUserModal,
      showUserReportsModal,
      selectedUserId
    }
  }
})
</script>